import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { Stack, Typography, alpha, useTheme } from "@mui/material";

import { PrimaryButton, SecondaryButton } from "@/components/common";

import { MarketplaceLineItemRow } from "./MarketplaceLineItemRow";
import { MarketplaceStoreGroupHeader } from "./MarketplaceStoreGroupHeader";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import type { CartItem, WooStoreConfig } from "../types";
import { formatPriceBrl } from "../utils/addressHelpers";
import {
  canIncreaseCartItemQuantity,
  getCartItemStockLimit,
} from "../utils/stockHelpers";
import { groupCartItemsByStore } from "../utils/storeGroupingHelpers";

interface WooCartDraftListProps {
  carts: Record<string, CartItem[]>;
  stores: WooStoreConfig[];
  checkoutLoading?: boolean;
  checkoutStoreId?: string | null;
  onUpdateDraftQuantity: (
    storeId: string,
    productId: number,
    quantity: number,
  ) => void;
  onCheckoutStore?: (storeId: string) => void;
  onExploreProducts?: () => void;
}

/** Lista do carrinho agrupada por loja. */
export function WooCartDraftList({
  carts,
  stores,
  checkoutLoading = false,
  checkoutStoreId = null,
  onUpdateDraftQuantity,
  onCheckoutStore,
  onExploreProducts,
}: WooCartDraftListProps) {
  const theme = useTheme();
  const groups = groupCartItemsByStore(carts, stores);
  const multiStore = groups.length > 1;

  if (groups.length === 0) {
    return (
      <Stack spacing={1.5} alignItems="center" sx={{ py: 4, textAlign: "center" }}>
        <ShoppingCartOutlinedIcon
          sx={{ fontSize: 48, color: alpha(theme.palette.primary800.main, 0.3) }}
        />
        <Typography sx={{ ...mt.body, color: "text.secondary" }}>
          Nenhum item no pedido.
        </Typography>
        {onExploreProducts ? (
          <SecondaryButton width="fit-content" height="44px" onClick={onExploreProducts}>
            Continuar comprando
          </SecondaryButton>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      {groups.map((group) => (
        <Stack key={group.store.id} spacing={1.25} sx={{ mb: 2.5 }}>
          <MarketplaceStoreGroupHeader
            store={group.store}
            metaLabel={`${group.itemCount} ${group.itemCount === 1 ? "item" : "itens"}`}
            trailing={formatPriceBrl(group.subtotal)}
          />
          {group.items.map((item) => (
            <MarketplaceLineItemRow
              key={item.productId}
              name={item.name}
              price={item.price}
              quantity={item.quantity}
              imageSrc={item.imageSrc}
              editable
              stockLimit={getCartItemStockLimit(item)}
              canIncrease={canIncreaseCartItemQuantity(item)}
              onChangeQuantity={(qty) =>
                onUpdateDraftQuantity(group.store.id, item.productId, qty)
              }
            />
          ))}
          {multiStore && onCheckoutStore ? (
            <PrimaryButton
              width="100%"
              height="44px"
              loading={checkoutLoading && checkoutStoreId === group.store.id}
              disabled={checkoutLoading && checkoutStoreId !== group.store.id}
              onClick={() => onCheckoutStore(group.store.id)}
            >
              Finalizar compra — {group.store.name}
            </PrimaryButton>
          ) : null}
        </Stack>
      ))}
    </Stack>
  );
}
